import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { imeiAPI } from '../api/client'
import { extractImeiDigits } from '../utils/extractImei'
import { preprocessImeiLabelDataUrl } from '../utils/imeiOcrPreprocess'

interface ImeiScannerModalProps {
  isOpen: boolean
  onClose: () => void
  onImeiFound: (digits: string) => void
  /** When set, local OCR can fall back to `POST /api/imei/ocr-extract` (Google Vision or OCR.space on server). */
  authToken?: string | null
}

async function runOcrOnDataUrl(dataUrl: string): Promise<string> {
  const { createWorker, PSM } = await import('tesseract.js')
  const worker = await createWorker('eng')
  await worker.setParameters({
    tessedit_char_whitelist: '0123456789IMEIimei:./- ',
    tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
  })
  try {
    const {
      data: { text },
    } = await worker.recognize(dataUrl)
    return text
  } finally {
    await worker.terminate()
  }
}

async function tryServerOcrFromDataUrl(dataUrl: string, token: string): Promise<string | null> {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  const file = new File([blob], 'imei-label.jpg', { type: blob.type || 'image/jpeg' })
  const fd = new FormData()
  fd.append('file', file)
  const r = await imeiAPI.ocrExtractFromImage(token, fd)
  return r.data?.imei ?? null
}

export default function ImeiScannerModal({
  isOpen,
  onClose,
  onImeiFound,
  authToken = null,
}: ImeiScannerModalProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')
  const [phase, setPhase] = useState<'idle' | 'camera' | 'ocr'>('idle')

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  useEffect(() => {
    if (!isOpen) {
      stopCamera()
      setError('')
      setPhase('idle')
      return
    }

    let cancelled = false
    setError('')
    setPhase('camera')

    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        const v = videoRef.current
        if (v) {
          v.srcObject = stream
          await v.play().catch(() => {})
        }
      } catch {
        if (!cancelled) {
          setError(t('listing.imeiScan.cameraDenied'))
          setPhase('idle')
        }
      }
    }

    start()
    return () => {
      cancelled = true
      stopCamera()
    }
  }, [isOpen, stopCamera, t])

  async function processDataUrl(dataUrl: string) {
    stopCamera()
    setError('')
    setPhase('ocr')
    try {
      let prepared = dataUrl
      try {
        prepared = await preprocessImeiLabelDataUrl(dataUrl)
      } catch {
        prepared = dataUrl
      }

      let imei: string | null = null
      if (authToken) {
        try {
          imei = await tryServerOcrFromDataUrl(prepared, authToken)
        } catch {
          // Server OCR unavailable or misconfigured
        }
      }
      if (!imei) {
        try {
          const text = await runOcrOnDataUrl(prepared)
          imei = extractImeiDigits(text)
        } catch {
          // Tesseract failed (blocked WASM, low memory, …)
        }
      }
      if (!imei) {
        setError(t('listing.imeiScan.noDigits'))
        setPhase('idle')
        return
      }
      onImeiFound(imei)
      onClose()
    } catch {
      setError(t('listing.imeiScan.ocrFailed'))
      setPhase('idle')
    }
  }

  function captureFrame() {
    const video = videoRef.current
    if (!video || video.readyState < 2) {
      setError(t('listing.imeiScan.videoNotReady'))
      return
    }
    const w = video.videoWidth
    const h = video.videoHeight
    if (!w || !h) {
      setError(t('listing.imeiScan.videoNotReady'))
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    stopCamera()
    void processDataUrl(dataUrl)
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = () => {
      const res = reader.result
      if (typeof res === 'string') void processDataUrl(res)
    }
    reader.readAsDataURL(file)
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay imei-scan-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-content modal-content--imei-scan"
        role="dialog"
        aria-labelledby="imei-scan-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="imei-scan-title">{t('listing.imeiScan.title')}</h3>
        <p className="imei-scan-hint">{t('listing.imeiScan.hint')}</p>

        {phase === 'camera' && !error && (
          <video ref={videoRef} className="imei-scan-video" playsInline muted autoPlay />
        )}

        {phase === 'ocr' && (
          <p className="imei-scan-status">{t('listing.imeiScan.reading')}</p>
        )}

        {error && <p className="error-text imei-scan-error">{error}</p>}

        <div className="imei-scan-actions modal-buttons">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()}>
            {t('listing.imeiScan.fromGallery')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={onFileChange}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={phase === 'ocr'}
            onClick={captureFrame}
          >
            {t('listing.imeiScan.capture')}
          </button>
        </div>
      </div>
    </div>
  )
}
